<template>
  <button
    :class="[$style.container, { [$style.desktop]: desktop }]"
    @click="$emit('toggle')"
  >
    <!-- SIGN UP / USERNAME -->
    <!-- ------------------ -->

    <div :class="$style.toggleTitle">
      <!-- LOGGED OUT STATE -->
      <template v-if="!user">
        <template v-if="!hideSignUp">
          <span>{{ signUpLabel }}</span>
          <span :class="$style.slash">/</span>
        </template>
        <span>{{ signInLabel }}</span>
      </template>

      <!-- LOGGED IN STATE -->
      <template v-else>
        <div
          v-if="user.photoURL"
          :class="$style.profileImage"
          :style="`background-image: url(${user.photoURL}`"
          data-testid="toggleProfileImage"
        />
        <NsIcon
          v-else
          :class="$style.profileIcon"
          icon-name="icon-profile"
          size="custom"
        />
        <div :class="$style.greeting" v-html="greetingText" />
      </template>
    </div>

    <!-- ARROW -->
    <!-- ----- -->

    <div :class="$style.toggleIcon">
      <NsIcon
        v-if="desktop"
        :class="[$style.arrowIcon, { [$style.open]: dropdownOpen }]"
        icon-name="icon-narrow-angle-bottom-24"
        size="custom"
      />
      <NsIcon
        v-else
        :class="[$style.arrowIcon, { [$style.open]: dropdownOpen }]"
        icon-name="icon-direction-mc"
      />
    </div>
  </button>
</template>

<script>
import { events } from "@nuskin/ns-util";
import { UserService, VGDistInfoService } from "@nuskin/ns-account";
import { NsIcon } from "@nuskin/design-components";

import vueEventBus from "./EventBus";

export default {
  name: "NsHeaderProfileToggle",
  components: {
    NsIcon
  },
  props: {
    /**
     * Dropdown open state
     */
    dropdownOpen: Boolean,
    /**
     * Greeting template from translations
     */
    greeting: {
      type: String,
      default: ""
    },
    /**
     * Hides the Signup text
     */
    hideSignUp: Boolean,
    /**
     * Sign in label
     */
    signInLabel: {
      type: String,
      default: ""
    },
    /**
     * Sign up label
     */
    signUpLabel: {
      type: String,
      default: ""
    },
    /**
     * Sign up session key
     */
    signUpSessionKey: {
      type: String,
      default: "preventSignupPrompt"
    }
  },
  data: () => ({
    preferredName: "",
    user: null
  }),
  computed: {
    desktop() {
      return this.$mq === "desktop" || this.$mq === "desktopHD";
    },
    greetingText() {
      const username =
        this.preferredName ||
        this.user.displayName ||
        this.user.localName ||
        this.user.lastName + ", " + this.user.firstName;
      const spaceReplace = this.desktop ? "&nbsp;" : "<br>";
      return this.greeting
        ? this.greeting
            .replace("%username%", username)
            .replace("<br>", spaceReplace)
        : username;
    }
  },
  mounted() {
    this.addListeners();
    this.setUser();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    setUser() {
      const isLoggedIn = UserService.isLoggedIn();
      if (!isLoggedIn) {
        this.user = null;
        return;
      }

      this.user = UserService.getUser();
      this.setUserProfileImageAndName();

      // Fallout Modal for Peru users
      if (
        this.user.accountType != 10 &&
        !sessionStorage[this.signUpSessionKey] &&
        this.user.countryCd == "PE" &&
        document.getElementById("signupPrompt") != null &&
        typeof window.showFalloutPrompt === "undefined" &&
        !this.user.noFalloutMsg
      ) {
        window.showFalloutPrompt = false;
        document.getElementById("signupPrompt").distributorSignupPath =
          "/content/nuskin/es_PE/sign-up/sign-up.html#/distributorsignup-minimal";
        setTimeout(() => {
          events.publish(
            events.account.OPEN_FALLOUT_MODAL,
            this.user.firstName
          );
        }, 1000);
      }
    },

    async setUserProfileImageAndName() {
      if (
        (!this.user.photoURL || !this.user.displayName) &&
        !this.user.isVolumesLoaded
      ) {
        let distInfo = await VGDistInfoService.getVGDistInformation();

        if (distInfo) {
          if (distInfo.photoURL && !this.user.photoURL) {
            this.user = {
              ...this.user,
              photoURL: distInfo.photoURL
            };
          }

          if (distInfo.displayName && !this.user.displayName) {
            this.user = {
              ...this.user,
              displayName: distInfo.displayName
            };
          }
        }
      }
    },

    handlePreferredNameUpdated(preferredName) {
      this.preferredName = preferredName;
    },

    handleProfileImageUpdated(profileImageUrl) {
      this.user = {
        ...this.user,
        photoURL: profileImageUrl
      };
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      // listen to config changes
      vueEventBus.$on("config-changed", this.setUser);

      // LOGIN EVENTS
      events.subscribe(events.authentication.LOGIN_COMPLETE, this.setUser);
      events.subscribe(events.authentication.LOGOUT, this.setUser);

      // USER UPDATES
      events.subscribe(
        events.account.PROFILE_IMAGE_UPDATED,
        this.handleProfileImageUpdated
      );

      events.subscribe(
        events.account.PREFERRED_NAME_UPDATE,
        this.handlePreferredNameUpdated
      );
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to event bus
      vueEventBus.$off();

      // LOGIN EVENTS
      events.unsubscribe(events.authentication.LOGIN_COMPLETE, this.setUser);
      events.unsubscribe(events.authentication.LOGOUT, this.setUser);

      // USER UPDATES
      events.unsubscribe(
        events.account.PROFILE_IMAGE_UPDATED,
        this.handleProfileImageUpdated
      );

      events.unsubscribe(
        events.account.PREFERRED_NAME_UPDATE,
        this.handlePreferredNameUpdated
      );
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';

* {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.container {
  @include global.subtitle-small-bold-caps;
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  margin: 0;
  padding-left: 15px;
  height: 60px;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.8px;
  color: map.get(colors.$themeColors, color-interactive-7);
  font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
  text-align: left;
  outline: none;
  cursor: pointer;
}

.toggleTitle {
  flex: 1;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.slash {
  padding: 0 5px;
}

.profileImage {
  border: 1px solid map.get(colors.$themeColors, color-interactive-8);
  border-radius: 50%;
  height: 26px;
  width: 26px;
  overflow: hidden;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  z-index: 1;
}

// nested for specificity
.toggleTitle .profileIcon {
  width: 26px;
  height: 26px;
}

.greeting {
  flex: 1;
  padding: 0 10px;
  line-height: 18px;
}

.toggleIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  cursor: pointer;
}

.arrowIcon {
  transition: map.get(other.$otherUtils, global-transition);

  &.open {
    transform: scaleY(-1);
  }
}

/** ************************* */
/** DESKTOP STYLE ADJUSTMENTS */
/** ************************* */

.container.desktop {
  padding: 0;
  height: auto;

  .profileImage {
    width: 20px;
    height: 20px;
  }

  .profileIcon {
    width: 20px;
    height: 20px;
  }

  .greeting {
    padding: 0 5px;

    br {
      display: none;
    }
  }

  .toggleIcon {
    margin-left: 10px;
    width: 14px;
    height: 14px;
  }

  .arrowIcon {
    width: 14px;
    height: 14px;
  }
}
</style>
